import * as THREE from 'three';

export interface MMLCube {
  id: string;
  width: number;
  height: number;
  depth: number;
  x: number;
  y: number;
  z: number;
  color: string;
  animations?: MMLAnimation[];
}

export interface MMLAnimation {
  attr: string;
  start: number;
  end: number;
  startTime: number;
  duration: number;
  pingPong: boolean;
  pingPongDelay: number;
}

export class MMLParser {
  // Fator de escala para aumentar o tamanho das parcelas
  static SCALE_FACTOR = 4.0;

  // Pequeno espaçamento entre parcelas para evitar z-fighting
  static PARCEL_GAP = 0.05;

  // Cor padrão para as parcelas (tom laranja/âmbar)
  static DEFAULT_COLOR = '#F7931A';

  static parse(xmlContent: string, applyScale: boolean = true): MMLCube[] {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlContent, "text/xml");
    const cubes: MMLCube[] = [];

    // Get all m-cube elements
    const cubeElements = xmlDoc.getElementsByTagName('m-cube');

    for (let i = 0; i < cubeElements.length; i++) {
      const cube = cubeElements[i];

      // Obter os valores originais
      const width = parseFloat(cube.getAttribute('width') || '1');
      const height = parseFloat(cube.getAttribute('height') || '1');
      const depth = parseFloat(cube.getAttribute('depth') || '1');
      const x = parseFloat(cube.getAttribute('x') || '0');
      const y = parseFloat(cube.getAttribute('y') || '0');
      const z = parseFloat(cube.getAttribute('z') || '0');

      // Aplicar o fator de escala se necessário
      const scaleFactor = applyScale ? this.SCALE_FACTOR : 1.0;

      // Aplicar um pequeno espaçamento entre as parcelas para evitar z-fighting
      const gap = this.PARCEL_GAP;

      const mmlCube: MMLCube = {
        id: cube.getAttribute('id') || `cube-${i}`,
        width: (width * scaleFactor) - gap,
        height: height * scaleFactor,
        depth: (depth * scaleFactor) - gap,
        x: (x * scaleFactor) + gap/2,
        y: y * scaleFactor,
        z: (z * scaleFactor) + gap/2,
        color: cube.getAttribute('color') || this.DEFAULT_COLOR,
        animations: []
      };

      // Parse animations if they exist
      const animElements = cube.getElementsByTagName('m-attr-anim');
      if (animElements.length > 0) {
        for (let j = 0; j < animElements.length; j++) {
          const anim = animElements[j];
          mmlCube.animations = mmlCube.animations || [];
          mmlCube.animations.push({
            attr: anim.getAttribute('attr') || 'y',
            start: parseFloat(anim.getAttribute('start') || '0'),
            end: parseFloat(anim.getAttribute('end') || '1'),
            startTime: parseFloat(anim.getAttribute('start-time') || '0'),
            duration: parseFloat(anim.getAttribute('duration') || '1000'),
            pingPong: anim.getAttribute('ping-pong') === 'true',
            pingPongDelay: parseFloat(anim.getAttribute('ping-pong-delay') || '0')
          });
        }
      }

      cubes.push(mmlCube);
    }

    return cubes;
  }

  // Helper method to convert MML cubes to collision barriers
  static convertToBarriers(cubes: MMLCube[]): any[] {
    return cubes.map(cube => {
      // Adicionar um pequeno buffer às barreiras para garantir que a nave não passe pelas bordas
      const buffer = this.PARCEL_GAP * 2;

      return {
        position: {
          x: cube.x - buffer/2,
          y: cube.z - buffer/2 // In our collision system, y is used for the z-coordinate
        },
        width: cube.width + buffer, // Adicionar buffer à largura para compensar o espaçamento
        depth: cube.depth + buffer, // Adicionar buffer à profundidade para compensar o espaçamento
        size: Math.max(cube.width, cube.depth) + buffer, // Manter para compatibilidade
        height: cube.height * 3 // Aumentar a altura da barreira para evitar que a nave passe por cima
      };
    });
  }

  // Helper method to get world dimensions from MML cubes
  static getWorldDimensions(cubes: MMLCube[]): { width: number, height: number } {
    if (cubes.length === 0) {
      return { width: 0, height: 0 };
    }

    let minX = cubes[0].x;
    let maxX = cubes[0].x + cubes[0].width;
    let minZ = cubes[0].z;
    let maxZ = cubes[0].z + cubes[0].depth;

    cubes.forEach(cube => {
      minX = Math.min(minX, cube.x);
      maxX = Math.max(maxX, cube.x + cube.width);
      minZ = Math.min(minZ, cube.z);
      maxZ = Math.max(maxZ, cube.z + cube.depth);
    });

    return {
      width: maxX - minX,
      height: maxZ - minZ
    };
  }
}
